import { ReactNode } from "react";

/** Labels/help/titles can be ReactNode; placeholders must be string */
type LabelNode = ReactNode;

export interface IRentersCopyUnit {
  appTitle: LabelNode;
  sectionHelpHeader: LabelNode;
  buttons: {
    previous: LabelNode;
    next: LabelNode;
    submit: LabelNode;
    wizardView: LabelNode;
    verticalView: LabelNode;
    demoToggle: LabelNode;
    completeBadge: LabelNode;
    langLabel: LabelNode;     // label near slider
    langEN: LabelNode;        // EN label
    langES: LabelNode;        // ES label
  };

  personal: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      firstName: { label: LabelNode; placeholder: string };
      lastName: { label: LabelNode; placeholder: string };
      dob: { label: LabelNode };
      email: { label: LabelNode; placeholder: string };
      phone: { label: LabelNode; placeholder: string };
    };
  };

  address: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      address1: { label: LabelNode; placeholder: string };
      address2: { label: LabelNode; placeholder: string };
      city: { label: LabelNode; placeholder: string };
      state: { label: LabelNode; placeholder: string };
      zip: { label: LabelNode; placeholder: string };
      moveInDate: { label: LabelNode };
    };
  };

  coverage: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      personalProperty: { label: LabelNode; placeholder: string };
      liabilityLimit: {
        label: LabelNode;
        placeholder: string;
        options: { _100k: LabelNode; _300k: LabelNode; _500k: LabelNode; custom: LabelNode };
      };
      deductible: { label: LabelNode; placeholder: string };
      waterBackup: {
        label: LabelNode;
        placeholder: string;
        options: { yes: LabelNode; no: LabelNode };
      };
      petLiability: {
        label: LabelNode;
        placeholder: string;
        options: { yes: LabelNode; no: LabelNode };
      };
    };
  };

  roommates: {
    title: LabelNode;
    help: LabelNode;
    hasRoommatesCheckbox: LabelNode;
    fields: {
      roommateCount: { label: LabelNode; placeholder: string };
      splitPolicy: {
        label: LabelNode;
        placeholder: string;
        options: { yes: LabelNode; no: LabelNode };
      };
    };
  };

  discounts: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      hasSprinklers: { label: LabelNode; placeholder: string; options: { yes: LabelNode; no: LabelNode } };
      hasAlarm: { label: LabelNode; placeholder: string; options: { yes: LabelNode; no: LabelNode } };
      bundleAuto: { label: LabelNode; placeholder: string; options: { yes: LabelNode; no: LabelNode } };
    };
  };

  contactPrefs: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      preferredContact: {
        label: LabelNode;
        placeholder: string;
        options: { email: LabelNode; phone: LabelNode; either: LabelNode };
      };
      notes: { label: LabelNode; placeholder: string };
    };
  };

  submitThankYou: {
    header: LabelNode;
    body: LabelNode;
  };
}

export interface IRentersCopy {
  /** English variants (add more entries as needed) */
  en: IRentersCopyUnit[];
  /** Spanish variants (add more entries as needed) */
  es: IRentersCopyUnit[];
}

/* ----------------------------- EN Variant 0 ----------------------------- */
const EN0: IRentersCopyUnit = {
  appTitle: <>🏠 Renters Insurance Quote</>,
  sectionHelpHeader: <>Section Help</>,
  buttons: {
    previous: <>Previous</>,
    next: <>Next</>,
    submit: <>Get My Quote</>,
    wizardView: <>Wizard View</>,
    verticalView: <>Vertical View</>,
    demoToggle: <>Demo Mode (auto-fill sample data)</>,
    completeBadge: <>Complete</>,
    langLabel: <>Language</>,
    langEN: <>English</>,
    langES: <>Español</>,
  },

  personal: {
    title: <>Personal Information</>,
    help: <>Your basic contact info so we can provide your quote.</>,
    fields: {
      firstName: { label: <>First Name</>, placeholder: "Your First name" },
      lastName: { label: <>Last Name</>, placeholder: "Your Last name" },
      dob: { label: <>Date of Birth</> },
      email: { label: <>Email</>, placeholder: "you@example.com" },
      phone: { label: <>Phone</>, placeholder: "(###) ###-####" },
    },
  },

  address: {
    title: <>Rental Address</>,
    help: <>Where is the place you’re renting?</>,
    fields: {
      address1: { label: <>Address Line 1</>, placeholder: "Your street address" },
      address2: { label: <>Address Line 2 (optional)</>, placeholder: "Apt / Unit" },
      city: { label: <>City</>, placeholder: "Your city" },
      state: { label: <>State</>, placeholder: "Select a state…" },
      zip: { label: <>ZIP Code</>, placeholder: "Your zip code" },
      moveInDate: { label: <>Move-In Date</> },
    },
  },

  coverage: {
    title: <>Coverage</>,
    help: <>Pick the coverage amounts you prefer (you can adjust later with an agent).</>,
    fields: {
      personalProperty: { label: <>Personal Property Amount ($)</>, placeholder: "For example: $30,000" },
      liabilityLimit: {
        label: <>Liability Limit</>,
        placeholder: "Select…",
        options: { _100k: <>$100,000</>, _300k: <>$300,000</>, _500k: <>$500,000</>, custom: <>Custom</> },
      },
      deductible: { label: <>Deductible ($)</>, placeholder: "For example: $500" },
      waterBackup: {
        label: <>Water Backup</>,
        placeholder: "Select…",
        options: { yes: <>Yes</>, no: <>No</> },
      },
      petLiability: {
        label: <>Pet Liability</>,
        placeholder: "Select…",
        options: { yes: <>Yes</>, no: <>No</> },
      },
    },
  },

  roommates: {
    title: <>Roommates</>,
    help: <>Turn on if you share the unit and want to reflect that in your quote.</>,
    hasRoommatesCheckbox: <>I have roommates</>,
    fields: {
      roommateCount: { label: <>Number of Roommates</>, placeholder: "e.g. 2" },
      splitPolicy: {
        label: <>Split Policy With Roommates?</>,
        placeholder: "Select…",
        options: { yes: <>Yes</>, no: <>No</> },
      },
    },
  },

  discounts: {
    title: <>Discounts</>,
    help: <>Let us know if your unit has safety features or if you want to bundle with auto.</>,
    fields: {
      hasSprinklers: { label: <>Sprinklers</>, placeholder: "Select…", options: { yes: <>Yes</>, no: <>No</> } },
      hasAlarm: { label: <>Monitored Alarm</>, placeholder: "Select…", options: { yes: <>Yes</>, no: <>No</> } },
      bundleAuto: { label: <>Bundle With Auto?</>, placeholder: "Select…", options: { yes: <>Yes</>, no: <>No</> } },
    },
  },

  contactPrefs: {
    title: <>Contact Preferences</>,
    help: <>Tell us how you’d like to be contacted and add any notes.</>,
    fields: {
      preferredContact: {
        label: <>Preferred Contact</>,
        placeholder: "Select…",
        options: { email: <>Email</>, phone: <>Phone</>, either: <>Either</> },
      },
      notes: { label: <>Notes</>, placeholder: "Anything else we should know?" },
    },
  },

  submitThankYou: {
    header: <>Thank you!</>,
    body: <>An agent will reach out to review and complete your application.</>,
  },
};

/* ----------------------------- ES Variant 0 ----------------------------- */
const ES0: IRentersCopyUnit = {
  appTitle: <>🏠 Cotización de Seguro para Inquilinos</>,
  sectionHelpHeader: <>Ayuda de la Sección</>,
  buttons: {
    previous: <>Anterior</>,
    next: <>Siguiente</>,
    submit: <>Obtener mi cotización</>,
    wizardView: <>Vista Asistente</>,
    verticalView: <>Vista Vertical</>,
    demoToggle: <>Modo Demo (completar automáticamente)</>,
    completeBadge: <>Completado</>,
    langLabel: <>Idioma</>,
    langEN: <>Inglés</>,
    langES: <>Español</>,
  },

  personal: {
    title: <>Información Personal</>,
    help: <>Tu información de contacto básica para preparar la cotización.</>,
    fields: {
      firstName: { label: <>Nombre</>, placeholder: "Ana" },
      lastName: { label: <>Apellido</>, placeholder: "Pérez" },
      dob: { label: <>Fecha de nacimiento</> },
      email: { label: <>Correo electrónico</>, placeholder: "tu@ejemplo.com" },
      phone: { label: <>Teléfono</>, placeholder: "(555) 555-1234" },
    },
  },

  address: {
    title: <>Dirección de la Vivienda Alquilada</>,
    help: <>¿Dónde se encuentra el lugar que alquilas?</>,
    fields: {
      address1: { label: <>Dirección (línea 1)</>, placeholder: "Av. Principal 123" },
      address2: { label: <>Dirección (línea 2, opcional)</>, placeholder: "Depto / Unidad" },
      city: { label: <>Ciudad</>, placeholder: "Boulder" },
      state: { label: <>Estado</>, placeholder: "Selecciona un estado…" },
      zip: { label: <>Código postal</>, placeholder: "80302" },
      moveInDate: { label: <>Fecha de mudanza</> },
    },
  },

  coverage: {
    title: <>Coberturas</>,
    help: <>Elige los montos de cobertura (podrás ajustarlos luego con un agente).</>,
    fields: {
      personalProperty: { label: <>Bienes personales (USD)</>, placeholder: "p. ej. 30000" },
      liabilityLimit: {
        label: <>Responsabilidad civil</>,
        placeholder: "Selecciona…",
        options: { _100k: <>$100,000</>, _300k: <>$300,000</>, _500k: <>$500,000</>, custom: <>Personalizado</> },
      },
      deductible: { label: <>Deducible (USD)</>, placeholder: "p. ej. 500" },
      waterBackup: {
        label: <>Respaldo de agua</>,
        placeholder: "Selecciona…",
        options: { yes: <>Sí</>, no: <>No</> },
      },
      petLiability: {
        label: <>Responsabilidad por mascotas</>,
        placeholder: "Selecciona…",
        options: { yes: <>Sí</>, no: <>No</> },
      },
    },
  },

  roommates: {
    title: <>Compañeros de cuarto</>,
    help: <>Actívalo si compartes la vivienda y quieres reflejarlo en la cotización.</>,
    hasRoommatesCheckbox: <>Tengo compañeros de cuarto</>,
    fields: {
      roommateCount: { label: <>Cantidad de compañeros</>, placeholder: "p. ej. 2" },
      splitPolicy: {
        label: <>¿Compartir la póliza?</>,
        placeholder: "Selecciona…",
        options: { yes: <>Sí</>, no: <>No</> },
      },
    },
  },

  discounts: {
    title: <>Descuentos</>,
    help: <>Indica si tu vivienda tiene medidas de seguridad o si deseas combinar con auto.</>,
    fields: {
      hasSprinklers: { label: <>Rociadores</>, placeholder: "Selecciona…", options: { yes: <>Sí</>, no: <>No</> } },
      hasAlarm: { label: <>Alarma monitoreada</>, placeholder: "Selecciona…", options: { yes: <>Sí</>, no: <>No</> } },
      bundleAuto: { label: <>¿Combinar con auto?</>, placeholder: "Selecciona…", options: { yes: <>Sí</>, no: <>No</> } },
    },
  },

  contactPrefs: {
    title: <>Preferencias de contacto</>,
    help: <>Indica cómo prefieres que te contactemos y agrega notas.</>,
    fields: {
      preferredContact: {
        label: <>Medio preferido</>,
        placeholder: "Selecciona…",
        options: { email: <>Correo</>, phone: <>Teléfono</>, either: <>Cualquiera</> },
      },
      notes: { label: <>Notas</>, placeholder: "¿Algo más que debamos saber?" },
    },
  },

  submitThankYou: {
    header: <>¡Gracias!</>,
    body: <>Un agente se pondrá en contacto para revisar y completar tu solicitud.</>,
  },
};

export const RENTERS_COPY: IRentersCopy = {
  en: [EN0], // add EN variants by pushing more IRentersCopyUnit items
  es: [ES0], // add ES variants by pushing more IRentersCopyUnit items
};
